import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UnallocatedMembersTable from './components/UnallocatedMembersTable';
import RemoveMemberModal from './components/RemoveMemberModal';
import BulkActionsBar from './components/BulkActionsBar';
import FilterPanel from './components/FilterPanel';
import httpHelper, { Method_TYPE } from '../../utils/http-helper';
import URL_CONSTANTS from '../../constants/url-constants';
import MessageModal from 'components/ui/MessageModal';
import { set } from 'date-fns';
import EditServicePage from './components/EditServiceModal';
import { calculateAge } from 'utils/calculateAge';

const REFRESH_INTERVAL_MS = 10000;

const UnallocatedMembersManagement = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("Member Enrollment Info");
  const [modelMessage, setModelMessage] = useState("");
  const [servicePointId, setServicePointId] = useState(null);
  const [showConfirmBtn, setShowConfirmBtn] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isInitialLoad = useRef(true);
  const loadMembersRef = useRef(null);

  useEffect(() => {
    const role = localStorage.getItem('role');

    if (!role) {
      navigate('/login-screen');
    }

    if(role == 'basic' || role == 'leader') {
      navigate('/dashboard');
    }
    
  }, [navigate]);

  const getNormalizedMembers = (memberDetails) => {
    return (memberDetails || [])
      .map((memberObj) => ({
        id: memberObj.member._id,
        name: memberObj.member.name || '',
        saiConnectId: memberObj.member.saiOneId,
        mobNo: memberObj.member.mobNoPrimary,
        age: calculateAge(memberObj.member.dob),
        district: memberObj.member?.district?.districtName || '------',
        samithi: memberObj.member?.samithi?.samithiName || '-----',
        createdAt: memberObj.createdAt,
        servicePointId: memberObj._id,
        is_blocked: Boolean(
          memberObj.member?.is_blocked === true ||
          memberObj.member?.is_blocked === 'true' ||
          memberObj.member?.is_blocked === 1 ||
          memberObj.member?.isBlocked === true ||
          memberObj.member?.isBlocked === 'true'
        ),
        rawMember: memberObj.member,
      }))
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
          numeric: true,
        })
      );
  };

  const loadMembers = useCallback(async ({ showLoader = false } = {}) => {
    try {
      if (showLoader || isInitialLoad.current) {
        setLoading(true);
      }

      // Cache-bust so the browser always hits the API on each poll
      const cacheBust = `_t=${Date.now()}`;
      const data = await httpHelper(
        `${URL_CONSTANTS.CURRENT_BATCH}?${cacheBust}`,
        Method_TYPE.GET
      );
      const currentBatch = data._id.toString();
      const memberDetails = await httpHelper(
        `${URL_CONSTANTS.SERVICE}/${currentBatch}/unallocated?${cacheBust}`,
        Method_TYPE.GET
      );
      const normalizedMembers = getNormalizedMembers(memberDetails);

      setMembers(normalizedMembers);
      setSelectedMembers((prev) =>
        prev.filter((id) => normalizedMembers.some((member) => member.id === id))
      );
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading unallocated members:', err);
    } finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  }, []);

  loadMembersRef.current = loadMembers;

  useEffect(() => {
    loadMembersRef.current?.({ showLoader: true });

    const intervalId = setInterval(() => {
      loadMembersRef.current?.({ showLoader: false });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  // Handle member selection
  const handleMemberSelect = (memberId, isSelected) => {
    setSelectedMembers(prev =>
      isSelected
        ? [...prev, memberId]
        : prev.filter(id => id !== memberId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    setSelectedMembers(isSelected ? members.map(m => m.id) : []);
  };

  // Handle remove member
  const removeMemberConfirmation = (member) => {
    setShowModal(true);
    setServicePointId(member.servicePointId.toString());
    setTitle("Remove Member");
    setModelMessage(`Are you sure you want to remove ${member.name} from the unallocated members list?`);
  };

  const handleRemoveMember = async () => {

    try {

         const body = {
          serviceId: servicePointId,
         }

         await httpHelper(URL_CONSTANTS.SERVICE_DELETE, Method_TYPE.POST, body);

         setServicePointId(null);
         setShowModal(true);
         setTitle("Member Removed");
         setModelMessage(`Member has been successfully removed from the service.`);
         setShowConfirmBtn(false);
         
         const data = await httpHelper(URL_CONSTANTS.CURRENT_BATCH, Method_TYPE.GET);

         const currentBatch = data._id.toString();
  
         const memberDetails = await httpHelper(`${URL_CONSTANTS.SERVICE}/${currentBatch}/unallocated`, Method_TYPE.GET);

         setShowConfirmBtn(true);
  
         const normalizedMembers = getNormalizedMembers(memberDetails);

         setMembers(normalizedMembers);
         //setTimeout(()=>window.location.reload(),2500);
    }
    catch (err) {
      setServicePointId(null);
      setTitle("Member Removed Failed");
      setModelMessage(`Failed to remove member from the service.`);
      setShowConfirmBtn(false);
      console.error(err);
    }

  }

  // Handle edit service
  const handleEditService = (member) => {

    setSelectedMember(member);
    setShowEditModal(true);
  }

  // Confirm remove member
  const confirmRemoveMember = async (member, reason) => {
    setActionLoading(true);

    // Simulate API call
    setTimeout(() => {
      setMembers(prev => prev.filter(m => m.id !== member.id));
      setSelectedMembers(prev => prev.filter(id => id !== member.id));
      setShowRemoveModal(false);
      setSelectedMember(null);
      setActionLoading(false);

      // Show success message (you can implement toast notifications)
      console.log(`Member ${member.name} removed successfully. Reason: ${reason}`);
    }, 1500);
  };


  // Handle bulk assign
  const handleBulkAssign = async (sector) => {
    setActionLoading(true);

    // Simulate API call
    setTimeout(() => {
      const selectedMemberObjects = members.filter(m => selectedMembers.includes(m.id));

      // Remove assigned members from unallocated list
      setMembers(prev => prev.filter(m => !selectedMembers.includes(m.id)));
      setSelectedMembers([]);
      setActionLoading(false);

      // Show success message
      console.log(`${selectedMemberObjects.length} members assigned to ${sector}`);
    }, 2000);
  };

  // Handle bulk remove
  const handleBulkRemove = async () => {
    if (window.confirm(`Are you sure you want to remove ${selectedMembers.length} selected members?`)) {
      setActionLoading(true);

      // Simulate API call
      setTimeout(() => {
        setMembers(prev => prev.filter(m => !selectedMembers.includes(m.id)));
        setSelectedMembers([]);
        setActionLoading(false);

        // Show success message
        console.log(`${selectedMembers.length} members removed successfully`);
      }, 1500);
    }
  };

  // Handle apply filters
  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    // In a real app, this would trigger an API call with filter parameters
    console.log('Applying filters:', filters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setActiveFilters({});
    // In a real app, this would reload the unfiltered data
    console.log('Clearing filters');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />

      <main className="pt-8 pb-8">

        {showEditModal ? <EditServicePage
          member={selectedMember}
          // onSave={saveServiceAssignment}
          loading={actionLoading}
          setShowEditModal={setShowEditModal}
        /> :
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                    <Icon name="UserX" size={24} className="text-error" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-heading font-semibold text-foreground">
                      Unallocated Members Management
                    </h1>
                    <p className="text-muted-foreground">
                      Manage volunteers awaiting service point assignment
                    </p>
                    {lastUpdated && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Auto-refresh 10s · Updated{' '}
                        {lastUpdated.toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => loadMembers({ showLoader: false })}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/add-edit-member')}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Add New Member
                </Button>

                {/* <Button
                variant="default"
                onClick={() => navigate('/dashboard')}
                iconName="LayoutDashboard"
                iconPosition="left"
              >
                Back to Dashboard
              </Button> */}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-error" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-error">
                      {members.length}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Unallocated Members
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-warning" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-warning">
                      {members.filter(m => {
                        const regDate = new Date(m.createdAt);
                        const daysDiff = (new Date() - regDate) / (1000 * 60 * 60 * 24);
                        return daysDiff > 7;
                      }).length}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Pending &gt; 7 Days
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="CheckSquare" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-primary">
                      {selectedMembers.length}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Selected Members
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            {/* <FilterPanel
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            activeFilters={activeFilters}
          /> */}

            {/* Bulk Actions Bar */}
            <BulkActionsBar
              selectedCount={selectedMembers.length}
              onBulkAssign={handleBulkAssign}
              onBulkRemove={handleBulkRemove}
              onClearSelection={() => setSelectedMembers([])}
              loading={actionLoading}
            />

            {/* Members Table */}
            <UnallocatedMembersTable
              members={members}
              onRemoveMember={removeMemberConfirmation}
              onEditService={handleEditService}
              selectedMembers={selectedMembers}
              onMemberSelect={handleMemberSelect}
              onSelectAll={handleSelectAll}
              loading={loading}
            />

            {/* Remove Member Modal */}
            <RemoveMemberModal
              isOpen={showRemoveModal}
              onClose={() => {
                setShowRemoveModal(false);
                setSelectedMember(null);
              }}
              member={selectedMember}
              onConfirm={confirmRemoveMember}
              loading={actionLoading}
            />


          </div>
        }

      </main>
      {/* Confirmation Modal for Remove Member */}
      <MessageModal
        isOpen={showModal}
        onClose={() => { setServicePointId(null); setShowModal(false) }}
        title={title}
        message={modelMessage}
        showConfirmBtn={showConfirmBtn}
        onConfirm={handleRemoveMember}
      />
      {/* <Footer /> */}
    </div>
  );
};

export default UnallocatedMembersManagement;